import { useEffect, useMemo, useReducer, useState } from "react";
import styled from "styled-components";
import { CalendarList } from "@lifeat/shared/calendar/types/CalendarList";
import { CalendarDetails } from "@lifeat/shared/data-models/calendar/CalendarDetails";
import {
    CalendarConnection,
    CalendarProvider,
} from "@lifeat/shared/data-models/user/integrations/IntegrationsDataEntity";
import { getErrorMessage } from "@lifeat/shared/error/ErrorUtil";
import { AmplitudeEventSource } from "@analytics/Amplitude/AmplitudeEvents";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import LoadingSpinner from "@common/LoadingSpinner/LoadingSpinner";
import { CallToActionContextType, useCallToActionContext } from "@contexts/CallToActionContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { logError } from "@service/ConsoleLoggerService";
import Styles from "@themes/Styles";
import { UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import { TaskListContext } from "../../../features/task-management/TaskListContext";
import useTaskData from "../../../features/task-management/useTaskData";
import { ChangeSelectedCalendarEvent } from "../AmplitudeCalendarEvents";
import { fetchCalendarLists, removeCalendarConnection, updateSelectedCalendarList } from "../CalendarService";
import CalendarSignInHeader from "./CalendarSignInHeader";
import CalendarView from "./CalendarView";

const CalendarWrapper = styled.div<{ show: boolean }>`
    display: ${(props) => (props.show ? "flex" : "none")};
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;

    height: 100%;
    border-radius: ${Styles.widgetBorderRadius};

    color: ${(props) => props.theme.textColorPrimary};
    background-color: ${(props) => props.theme.backgroundPrimary};
`;

const StyledCalendarDayView = styled(CalendarView)`
    overflow: scroll;
`;

const LoadingDiv = styled.div`
    margin-top: 10px;
`;

const INIT_SELECTED_CALENDAR_STATE: CalendarDetails[] = [];

const CALENDAR_CONNECTION_ERROR_MSG = "Error connecting your calendar. Please refresh the app.";

interface CalendarSidebarProps {
    /**
     * If the component should be displayed. Component is still rendered,
     * but possibly hidden using CSS.
     */
    readonly show: boolean;
    /**
     * Where the event was triggered from in the app.
     */
    readonly amplitudeEventSource: AmplitudeEventSource;
    /**
     * How the calendar should display. By default is set to "day".
     */
    readonly calendarView?: "day" | "week";
    /**
     * If true, emit a JavaScript notification for the upcoming event.
     * By default, notifications will not be emitted.
     * This was added as a simple fix for preventing multiple of the same
     * notifications for being emitted when this component is rendered in
     * multiple places.
     */
    readonly sendCalendarNotifications?: boolean;
    className?: string;
}
function CalendarSidebar(props: CalendarSidebarProps) {
    const amplitudeService = AmplitudeService.getInstance();

    const { setUpgradeCtaContext }: CallToActionContextType = useCallToActionContext();
    const { user, userData }: UserContextType = useUserContext();
    const taskData = useTaskData({
        uid: user?.uid,
    });

    function selectedCalendarReducer(state: CalendarDetails[], newState: CalendarDetails[]) {
        // filter out any null entries
        const filteredList = newState?.filter((value) => value !== null && value !== undefined);
        return filteredList ? [...filteredList] : [];
    }

    const [isLoadingCalendarList, setIsLoadingCalendarList] = useState(false);
    const [calendars, setCalendars] = useState<CalendarList>({
        [CalendarProvider.GOOGLE]: {},
        [CalendarProvider.OUTLOOK]: {},
    });
    const calendarCount = useMemo(() => {
        let total = 0;
        for (const groupByProvider of Object.values(calendars)) {
            for (const groupByEmail of Object.values(groupByProvider)) {
                for (const _calendar of Object.values(groupByEmail)) {
                    total++;
                }
            }
        }
        return total;
    }, [calendars]);

    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const [selectedCalendarState, selectedCalendarDispatch] = useReducer(
        selectedCalendarReducer,
        INIT_SELECTED_CALENDAR_STATE,
    );

    const selectedCalendarsFromDb: () => CalendarDetails[] = () => {
        const ret: CalendarDetails[] = [];

        for (const connection of userData?.integrationsData?.calendar ?? []) {
            for (const id of connection.selectedCalendarIds ?? []) {
                const calendar = calendars[connection.provider][connection.email]?.[id];
                if (calendar) {
                    ret.push(calendar);
                }
            }
        }

        return ret;
    };

    useEffect(() => {
        selectedCalendarDispatch(selectedCalendarsFromDb());
    }, [calendars]);

    useEffect(() => {
        if (user && calendarCount === 0) {
            if (true) {
                setIsLoadingCalendarList(true);

                fetchCalendarLists()
                    .then((calendarsData) => {
                        if (calendarsData != null) {
                            setCalendars(calendarsData);
                        }
                        setIsLoadingCalendarList(false);
                    })
                    .catch((e: unknown) => {
                        setErrorMsg(CALENDAR_CONNECTION_ERROR_MSG);
                        logError(`Error fetching calendar list: ${getErrorMessage(e)}`);

                        setIsLoadingCalendarList(false);
                    });
            }
        }
    }, [user]);

    const handleChangeCalendar = (calendar: CalendarDetails, selected: boolean) => {
        if (user && userData) {
            const isPro = userData?.proUserInfo?.isProUser ?? false;
            if (!calendar.isPrimary && !isPro) {
                setUpgradeCtaContext("multipleCalendars", UpgradeCTASource.CALENDAR_SELECTOR);
            } else {
                let clone = [...selectedCalendarState];
                if (selected) {
                    clone.push(calendar);
                } else {
                    clone = clone.filter(
                        (x) =>
                            x.provider !== calendar.provider ||
                            x.email !== calendar.email ||
                            x.calendarId !== calendar.calendarId,
                    );
                }

                selectedCalendarDispatch(clone);
                amplitudeService.logEvent(new ChangeSelectedCalendarEvent());

                // get only list of IDs to store in DB
                updateSelectedCalendarList({ calendar, selected });
            }
        }
    };

    const handleRemoveCalendarConnection = async (connection: CalendarConnection) => {
        setCalendars((before) => {
            const clone = structuredClone(before);
            delete clone[connection.provider][connection.email];
            return clone;
        });
        await removeCalendarConnection(connection);
    };

    return (
        <TaskListContext.Provider value={taskData}>
            <CalendarWrapper show={props.show} className={props.className}>
                {isLoadingCalendarList && (
                    <LoadingDiv>
                        <LoadingSpinner size={20} />
                    </LoadingDiv>
                )}

                {calendarCount === 0 && props.calendarView !== "week" && <CalendarSignInHeader />}

                <StyledCalendarDayView
                    amplitudeEventSource={props.amplitudeEventSource}
                    selectedCalendarList={selectedCalendarState}
                    calendars={calendars}
                    onSelectedCalendarChange={handleChangeCalendar}
                    onCalendarConnectionRemove={handleRemoveCalendarConnection}
                    isOnlyPrimary={
                        selectedCalendarState?.length === 1 &&
                        selectedCalendarState[0] &&
                        calendars[selectedCalendarState[0].provider][selectedCalendarState[0].email]?.[
                            selectedCalendarState[0].calendarId
                        ]?.isPrimary
                    }
                    errorMsg={errorMsg}
                    calendarView={props.calendarView ?? "day"}
                    sendNotifications={props.sendCalendarNotifications}
                    show={props.show}
                />
            </CalendarWrapper>
        </TaskListContext.Provider>
    );
}

export default CalendarSidebar;
